import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/Form';
import { Input } from '../../../components/ui/Input';
import { usePageTitle } from '../../../hooks/shared';
import { useAuthStore } from '../store/authStore';

export default function Signup() {
  usePageTitle('Create Account');
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState('');
  const { register: registerUser, error, clearError, isAuthenticated } = useAuthStore();

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const validatePasswordStrength = (value) => {
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    if (!hasUpperCase) return 'Password must contain at least one uppercase letter';
    if (!hasLowerCase) return 'Password must contain at least one lowercase letter';
    if (!hasNumber) return 'Password must contain at least one number';
    if (!hasSpecialChar) return 'Password must contain at least one special character';
    return true;
  };

  const onSubmit = async (data) => {
    // Remove confirmPassword from the data sent to API
    const { confirmPassword: _confirmPassword, zipCode, ...userData } = data;
    if (zipCode) userData.zipCode = zipCode;

    await registerUser(userData);

    // Show success message
    setSuccessMessage(
      'Account created successfully! Please check your email to verify your account.'
    );

    // Redirect to login after 3 seconds
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  };

  return (
    <Card className="w-full max-w-md" patrioticShadow>
      <div className="text-center mb-8">
        <h1 className="text-3xl font-serif font-bold mb-2">Create Your Account</h1>
        <p className="text-neutral-600">Join VotePlan and get started today</p>
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              rules={{
                required: 'First name is required',
                minLength: {
                  value: 1,
                  message: 'First name is required',
                },
                maxLength: {
                  value: 100,
                  message: 'First name is too long',
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              rules={{
                required: 'Last name is required',
                minLength: {
                  value: 1,
                  message: 'Last name is required',
                },
                maxLength: {
                  value: 100,
                  message: 'Last name is too long',
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            rules={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
              maxLength: {
                value: 255,
                message: 'Email is too long',
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" {...field} />
                </FormControl>
                <FormDescription>We'll send you a verification email</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            rules={{
              required: 'Password is required',
              minLength: {
                value: 10,
                message: 'Password must be at least 10 characters',
              },
              validate: validatePasswordStrength,
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="Create a strong password" {...field} />
                </FormControl>
                <FormDescription>
                  At least 10 characters with uppercase, lowercase, number, and special character
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            rules={{
              required: 'Please confirm your password',
              validate: (value) => value === form.getValues('password') || 'Passwords do not match',
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="Re-enter your password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="zipCode"
            rules={{
              pattern: {
                value: /^\d{5}(-\d{4})?$/,
                message: 'Invalid ZIP code format',
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>ZIP Code (Optional)</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="12345" {...field} />
                </FormControl>
                <FormDescription>
                  Used to find your polling location and ballot information
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !!successMessage}
            className="w-full"
          >
            {form.formState.isSubmitting ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center">
        <p className="text-neutral-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Log in
          </Link>
        </p>
      </div>

      {/* Divider */}
      <div className="mt-8 pt-6 border-t border-neutral-200">
        <p className="text-center text-sm text-neutral-500">
          <Link to="/" className="hover:text-primary-600">
            ← Back to home
          </Link>
        </p>
      </div>
    </Card>
  );
}
